import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { api, ErroApi, registrarSessaoExpirada } from './api';

export type Papel = 'admin' | 'editor' | 'viewer' | 'lider' | 'lider_adm' | 'voluntario' | 'membro';

export type Usuario = {
  id: number;
  nome: string;
  email: string;
  papel: Papel;
  membroId: number | null;
  // a pessoa lidera alguma equipe de discipulado
  lideraEquipe?: boolean;
  // a pessoa e da administracao ou serve na midia: ve a area de Quadros
  podeQuadro?: boolean;
};

type Contexto = {
  usuario: Usuario | null;
  carregando: boolean;
  avisoSessao: string | null;
  limparAviso: () => void;
  entrar: (email: string, senha: string) => Promise<void>;
  sair: () => Promise<void>;
};

const AutenticacaoContexto = createContext<Contexto | null>(null);

export function ProvedorAutenticacao({ children }: { children: React.ReactNode }) {
  const [usuario, setUsuario] = useState<Usuario | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [avisoSessao, setAvisoSessao] = useState<string | null>(null);

  /**
   * 401 tratado em um lugar so: qualquer chamada que voltar 401 derruba o
   * usuario e mostra "Sua sessão expirou, entre de novo" — nunca um erro generico.
   */
  useEffect(() => {
    registrarSessaoExpirada((mensagem) => {
      setUsuario((atual) => {
        if (atual) setAvisoSessao(mensagem);
        return null;
      });
    });
  }, []);

  useEffect(() => {
    let vivo = true;
    api<{ usuario: Usuario }>('/auth/eu')
      .then((d) => vivo && setUsuario(d.usuario))
      .catch((e: ErroApi) => {
        // 401 aqui e o caso normal de quem ainda nao entrou: nao e aviso de sessao caida
        if (vivo && e.status !== 401) console.error('[auth] falha ao carregar sessão', e);
      })
      .finally(() => vivo && setCarregando(false));
    return () => {
      vivo = false;
    };
  }, []);

  const entrar = useCallback(async (email: string, senha: string) => {
    const d = await api<{ usuario: Usuario }>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, senha }),
    });
    setAvisoSessao(null);
    setUsuario(d.usuario);
  }, []);

  const sair = useCallback(async () => {
    try {
      await api('/auth/logout', { method: 'POST' });
    } finally {
      setUsuario(null);
    }
  }, []);

  const valor = useMemo(
    () => ({
      usuario,
      carregando,
      avisoSessao,
      limparAviso: () => setAvisoSessao(null),
      entrar,
      sair,
    }),
    [usuario, carregando, avisoSessao, entrar, sair],
  );

  return <AutenticacaoContexto.Provider value={valor}>{children}</AutenticacaoContexto.Provider>;
}

export function useAutenticacao(): Contexto {
  const ctx = useContext(AutenticacaoContexto);
  if (!ctx) throw new Error('useAutenticacao precisa estar dentro de ProvedorAutenticacao');
  return ctx;
}

export const ehEquipe = (p: Papel | undefined) => p === 'admin' || p === 'editor' || p === 'viewer';
/** Serve em algum ministerio: ve a escala do ministerio dele. */
export const serveEmMinisterio = (p: Papel | undefined) => ehEquipe(p) || p === 'voluntario';
export const podeEscrever = (p: Papel | undefined) => p === 'admin' || p === 'editor';
